import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { RootState } from '../../../../../../../@types/RootState';
import { Layout, LoadingSpinerArea } from '../../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../../components/AlertWithTimestamp';
import {
  addSelectedCattegories,
  CacheDataItem,
  RegisterLimitStoreCattegoryKeys,
  resetCattegory,
  setCacheByKey as setCattegoryCacheByKey,
  setRows as setCattegoryRows,
  setValueByKey as setCattegoryValueByKey,
} from '../../../../../../../reducers/ipa/limits/filters/cattegory';
import { resetSegment, setSelectType as setSegmentSelectType, setSegmentValueByKey } from '../../../../../../../reducers/ipa/limits/filters/segment';
import {
  resetStore,
  setStoreCacheByKey,
  setSelectType as setStoreSelectType,
  setStoreValueByKey,
  StoreTypeWithoutAllStores,
} from '../../../../../../../reducers/ipa/limits/filters/store';
import { SegmentsCurve } from '../../../RegrasDePreco/lib';
import { StoreType } from '../../../RegrasDePreco/lib/data';
import RegisterLimitFormActions from '../../components/RegisterLimitFormActions/RegisterLimitFormActions';
import RegisterLimitPageFilters from '../../components/RegisterLimitPageFilters/RegisterLimitPageFilters';
import RegisterLimitPageForm from '../../components/RegisterLimitPageForm/RegisterLimitPageForm';
import RegisterLimitPageHeader from '../../components/RegisterLimitPageHeader/RegisterLimitPageHeader';
import { GetLimitDataParsedResponse, getRegisterLimitModel } from '../../lib/data';
import { registerLimitSchema, RegisterLimitFormValues, RegisterLimitSchemaProps } from '../../lib/schema';
import pageStyles from '../RegisterLimitPage/RegisterLimitPage.module.scss';
import { extractApiErrorMessage } from '../../../../../../../utils/extractApiErrorMessage';
import { getLimitDataQuery, updateLimitData } from './service';

type EditLimitPageProps = RouteComponentProps<{ id: string }> & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const hasFormValue = (value: string | null | undefined) => value !== null && value !== undefined && value.trim() !== '';

const EditLimitPage = memo(
  ({
    match: {
      params: { id: limitId },
    },
    history,
    filters,
    setStoreValueByKey,
    setStoreSelectType,
    setStoreCacheByKey,
    setSegmentSelectType,
    setSegmentValueByKey,
    setCattegoryRows,
    addSelectedCattegories,
    setCattegoryCacheByKey,
    setCattegoryValueByKey,
    clearFilters,
  }: EditLimitPageProps) => {
    const handleSuccess = useCallback(
      ({ filters }: GetLimitDataParsedResponse) => {
        const handleUpdateStore = ({ store }: GetLimitDataParsedResponse['filters']) => {
          const { key, value, cache } = store;
          if (key) setStoreSelectType(key);
          if (value) setStoreValueByKey(key as StoreTypeWithoutAllStores, value);
          if (cache) setStoreCacheByKey(key as StoreTypeWithoutAllStores, cache);
        };

        const handleUpdateSegment = ({ segments }: GetLimitDataParsedResponse['filters']) => {
          const { key, value } = segments;
          if (key) setSegmentSelectType(key as SegmentsCurve);
          if (value) setSegmentValueByKey(key as SegmentsCurve, value);
        };

        const handleUpdateCattegory = ({ cattegory }: GetLimitDataParsedResponse['filters']) => {
          const { rows, data } = cattegory;
          setCattegoryRows(rows);
          Object.entries(data).forEach(([key, value]) => {
            addSelectedCattegories(key as RegisterLimitStoreCattegoryKeys);
            setCattegoryCacheByKey(key as RegisterLimitStoreCattegoryKeys, value.cache);
            setCattegoryValueByKey(key as RegisterLimitStoreCattegoryKeys, value.value);
          });
        };

        handleUpdateCattegory(filters);
        handleUpdateStore(filters);
        handleUpdateSegment(filters);
      },
      [
        addSelectedCattegories,
        setCattegoryCacheByKey,
        setCattegoryRows,
        setSegmentSelectType,
        setSegmentValueByKey,
        setStoreCacheByKey,
        setStoreSelectType,
        setStoreValueByKey,
        setCattegoryValueByKey,
      ],
    );

    const { data } = useQuery({ ...getLimitDataQuery(limitId), onSuccess: handleSuccess });

    const form = useForm<RegisterLimitFormValues, unknown, RegisterLimitSchemaProps>({
      resolver: zodResolver(registerLimitSchema),
      defaultValues: {
        name: '',
        marginType: 'margin',
        adjustPricesAccordingToLimits: false,
        minMargin: null,
        maxMargin: null,
        downwardVariation: null,
        upwardVariation: null,
        minCpi: null,
        maxCpi: null,
      },
      values: {
        name: data?.form.name ?? '',
        marginType: data?.form.marginType ?? 'margin',
        adjustPricesAccordingToLimits: data?.form.adjustPricesAccordingToLimits ?? false,
        minMargin: data?.form.minMargin ?? null,
        maxMargin: data?.form.maxMargin ?? null,
        downwardVariation: data?.form.downwardVariation ?? null,
        upwardVariation: data?.form.upwardVariation ?? null,
        minCpi: data?.form.minCpi ?? null,
        maxCpi: data?.form.maxCpi ?? null,
      },
    });

    const { marginType, minMargin, maxMargin, downwardVariation, upwardVariation, minCpi, maxCpi } = form.watch();
    const marginValues = marginType === 'objectiveMargin' ? [downwardVariation, upwardVariation] : [minMargin, maxMargin];
    const hasAnyLimit = [...marginValues, minCpi, maxCpi].some(hasFormValue);
    const isDisabledSubmit = useMemo(() => !hasAnyLimit || form.formState.isSubmitting, [form.formState.isSubmitting, hasAnyLimit]);

    const handleSubmit = useCallback(
      async (formData: RegisterLimitSchemaProps) => {
        try {
          const model = getRegisterLimitModel({ formData, filters }, limitId);
          const { status } = await updateLimitData(limitId, model);
          if (![200, 201].includes(status)) return;
          clearFilters();
          form.reset();
          history.push('/ipa/limites-preco');
        } catch (err) {
          const message = extractApiErrorMessage(err) || 'Erro ao salvar limite. Tente novamente.';
          AlertWithTimestamp({ message });
        }
      },
      [filters, limitId, clearFilters, form, history],
    );

    useEffect(() => {
      clearFilters();
    }, [clearFilters]);

    return (
      <Layout className={pageStyles.wrapper}>
        <Layout.Body asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} className={pageStyles.body}>
            <RegisterLimitPageHeader form={form} />
            <RegisterLimitPageFilters />
            <RegisterLimitPageForm form={form} />
            <RegisterLimitFormActions isDisabledSubmit={isDisabledSubmit} />
          </form>
        </Layout.Body>
        <LoadingSpinerArea />
      </Layout>
    );
  },
);

EditLimitPage.displayName = 'EditLimitPage';

const mapStateToProps = (state: RootState) => ({ filters: state.ipa.limits.filters });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setStoreValueByKey: (key: StoreTypeWithoutAllStores, value: string[]) => {
    dispatch(setStoreValueByKey({ key, value }));
  },
  setStoreSelectType: (value: StoreType) => {
    dispatch(setStoreSelectType(value));
  },
  setStoreCacheByKey: (key: StoreTypeWithoutAllStores, value: DataItem[]) => {
    dispatch(setStoreCacheByKey({ key, value }));
  },
  setSegmentValueByKey: (key: SegmentsCurve, value: string[]) => {
    dispatch(setSegmentValueByKey({ key, value }));
  },
  setSegmentSelectType: (value: SegmentsCurve) => {
    dispatch(setSegmentSelectType(value));
  },
  setCattegoryRows: (rows: number) => {
    dispatch(setCattegoryRows(rows));
  },
  addSelectedCattegories: (key: RegisterLimitStoreCattegoryKeys) => {
    dispatch(addSelectedCattegories(key));
  },
  setCattegoryCacheByKey: (key: RegisterLimitStoreCattegoryKeys, cache: CacheDataItem[]) => {
    dispatch(setCattegoryCacheByKey({ key, cache }));
  },
  setCattegoryValueByKey: (key: RegisterLimitStoreCattegoryKeys, value: string[]) => {
    dispatch(setCattegoryValueByKey({ key, value }));
  },
  clearFilters: () => {
    dispatch(resetSegment());
    dispatch(resetStore());
    dispatch(resetCattegory());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditLimitPage));
